import React, { useState, useEffect } from 'react';
import { Printer, Edit3, Calendar, Search, Filter } from 'lucide-react';
import { meetingService } from '../utils/supabaseData';
import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';
import { attendeeListService } from '../utils/attendeeListService';
import MobileLoadingSpinner from '../components/MobileLoadingSpinner';
import PrintableAttendeeList from '../components/PrintableAttendeeList';
import PullToRefresh from '../components/PullToRefresh';

const OrderManagementView = ({ currentUser, setCurrentView, setSelectedMeetingId }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active' (non-cancelled), 'cancelled'
  const [savingStatus, setSavingStatus] = useState({}); // Track saving state per meeting
  const [attendeeListData, setAttendeeListData] = useState(null); // For print functionality
  const [loadingAttendeeList, setLoadingAttendeeList] = useState(false);
  const [editingCost, setEditingCost] = useState({}); // Track which cost fields are being edited

  // Modal states
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showRestaurantModal, setShowRestaurantModal] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  // Helper function to parse date strings as local dates (avoiding timezone issues)
  const parseLocalDate = (dateString) => {
    if (!dateString) return new Date();
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      return new Date(dateString);
    }
  };

  // Load upcoming meetings
  useEffect(() => {
    loadMeetings();
  }, [currentUser]);

  const loadMeetings = async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);
      const allMeetings = await meetingService.getAllMeetings(currentUser.id, currentUser.user_type);

      // Filter for upcoming meetings (today and future)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const upcomingMeetings = allMeetings.filter(meeting => {
        const meetingDate = parseLocalDate(meeting.meeting_date || meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= today;
      });

      // Sort by date and time chronologically (earliest first, with time as tiebreaker)
      upcomingMeetings.sort((a, b) => {
        const dateA = parseLocalDate(a.meeting_date || a.date);
        const dateB = parseLocalDate(b.meeting_date || b.date);

        // First compare dates
        const dateDiff = dateA - dateB;
        if (dateDiff !== 0) {
          return dateDiff;
        }

        // If dates are the same, compare times
        const timeA = a.start_time || '00:00:00';
        const timeB = b.start_time || '00:00:00';
        return timeA.localeCompare(timeB);
      });

      // Enrich with customer/provider details
      const enrichedMeetings = await Promise.all(upcomingMeetings.map(async meeting => {
        let customerName = 'Healthcare Provider';
        let customerPhone = '';
        let customerData = null;

        // Fetch customer details
        if (meeting.customer_id) {
          try {
            const { data: fetchedData } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name, phone, email, company, specialty, title, npi_number')
              .eq('id', meeting.customer_id)
              .single();

            if (fetchedData) {
              customerName = `${fetchedData.first_name || ''} ${fetchedData.last_name || ''}`.trim();
              customerPhone = fetchedData.phone || '';
              customerData = fetchedData; // Store full customer data
            }
          } catch (error) {
            logger.warn('Error fetching customer details', { error: error.message });
          }
        }

        return {
          ...meeting,
          customerName,
          customerPhone,
          customerData, // Add full customer data for modal
          // Ensure lunch_ordered field exists (default to false)
          lunch_ordered: meeting.lunch_ordered || false
        };
      }));

      setMeetings(enrichedMeetings);
    } catch (error) {
      logger.error('Error loading meetings', { error: error.message });
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle lunch ordered checkbox toggle with auto-save
  const handleLunchOrderedToggle = async (meetingId, currentValue) => {
    try {
      // Optimistically update UI
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, lunch_ordered: !currentValue } : m
      ));

      // Show saving indicator
      setSavingStatus(prev => ({ ...prev, [meetingId]: 'saving' }));

      // Save to Supabase
      const { error } = await supabase
        .from('meetings')
        .update({ lunch_ordered: !currentValue })
        .eq('id', meetingId);

      if (error) {
        throw error;
      }

      // Show success indicator briefly
      setSavingStatus(prev => ({ ...prev, [meetingId]: 'saved' }));
      setTimeout(() => {
        setSavingStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[meetingId];
          return newStatus;
        });
      }, 1500);

      logger.debug('Lunch ordered status updated', { meetingId, value: !currentValue });
    } catch (error) {
      logger.error('Error updating lunch ordered status', { error: error.message, meetingId });

      // Revert optimistic update on error
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, lunch_ordered: currentValue } : m
      ));

      // Show error indicator
      setSavingStatus(prev => ({ ...prev, [meetingId]: 'error' }));
      setTimeout(() => {
        setSavingStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[meetingId];
          return newStatus;
        });
      }, 2000);
    }
  };

  // Save cost to database on blur
  const handleCostUpdate = async (meetingId, newCost) => {
    try {
      // Parse and format the cost value
      let costValue = newCost === '' || newCost === null ? null : parseFloat(newCost);

      // If we have a valid number, format it to 2 decimal places
      if (costValue !== null && !isNaN(costValue)) {
        costValue = parseFloat(costValue.toFixed(2));
      }

      logger.debug('Saving cost to database', { meetingId, cost: costValue });

      // Save to Supabase
      const { error } = await supabase
        .from('meetings')
        .update({ actual_cost: costValue })
        .eq('id', meetingId);

      if (error) {
        throw error;
      }

      // Update local state with parsed numeric value (formatted to 2 decimals)
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, actual_cost: costValue } : m
      ));

      logger.debug('Cost saved successfully', { meetingId, cost: costValue });
    } catch (error) {
      logger.error('Error updating cost', { error: error.message, meetingId });
    }
  };

  // Navigate to edit meeting
  const handleEdit = (meetingId) => {
    setSelectedMeetingId(meetingId);
    setCurrentView('meeting-detail');
  };

  // Print meeting details with attendee list (same as SalesMeetingDetailView)
  const handlePrintAttendeeList = async (meetingId) => {
    try {
      setLoadingAttendeeList(true);
      logger.debug('Fetching attendee list for printing', { meetingId });

      const attendeeData = await attendeeListService.getAttendeeList(meetingId);
      setAttendeeListData(attendeeData);

      // Wait a moment for React to render the PrintableAttendeeList component
      setTimeout(() => {
        window.print();
        setLoadingAttendeeList(false);
      }, 100);
    } catch (error) {
      logger.error('Error loading attendee list for printing', { error: error.message });
      alert('Error loading attendee list. Please try again.');
      setLoadingAttendeeList(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBD';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Clean phone number for tel: links (iOS requires clean numbers)
  const cleanPhoneForTel = (phone) => {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
  };

  // Format cost for display (always show 2 decimal places)
  const formatCostForDisplay = (cost) => {
    if (cost === null || cost === undefined || cost === '') return '';
    const numCost = parseFloat(cost);
    return isNaN(numCost) ? '' : numCost.toFixed(2);
  };

  // Format currency with commas (e.g., 1234.56 -> "1,234.56")
  const formatCurrencyWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Remove commas from formatted currency string (e.g., "1,234.56" -> "1234.56")
  const removeCommas = (value) => {
    return value.replace(/,/g, '');
  };

  // Helper function to get status color classes (matching EnhancedMeetingCard)
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'declined':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Filter meetings based on search, date filter, and status filter
  const filteredMeetings = meetings.filter(meeting => {
    // Search filter
    const searchMatch = searchTerm === '' ||
      meeting.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.restaurant_name?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!searchMatch) return false;

    // Status filter
    if (statusFilter === 'active') {
      // Show all non-cancelled meetings (confirmed, pending, declined, etc.)
      if (meeting.status === 'cancelled') return false;
    } else if (statusFilter === 'cancelled') {
      // Show only cancelled meetings
      if (meeting.status !== 'cancelled') return false;
    }
    // 'all' shows everything (confirmed, pending, cancelled, declined)

    // Date filter
    if (dateFilter === 'all') return true;

    const meetingDate = parseLocalDate(meeting.meeting_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'today') {
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate.getTime() === today.getTime();
    }

    if (dateFilter === 'week') {
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      return meetingDate >= today && meetingDate <= weekFromNow;
    }

    if (dateFilter === 'month') {
      const monthFromNow = new Date(today);
      monthFromNow.setMonth(monthFromNow.getMonth() + 1);
      return meetingDate >= today && meetingDate <= monthFromNow;
    }

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <MobileLoadingSpinner />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={loadMeetings} disabled={loading}>
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="mb-3 sm:mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Order Management</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Manage upcoming meeting orders and logistics</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
            <input
              type="text"
              placeholder="Search by customer or restaurant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ paddingLeft: '44px' }}
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="text-gray-400 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active Only</option>
              <option value="all">All Meetings</option>
              <option value="cancelled">Cancelled Only</option>
            </select>
          </div>

          {/* Date Filter */}
          <div className="flex items-center space-x-2">
            <Calendar className="text-gray-400 w-5 h-5" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Upcoming</option>
              <option value="today">Today</option>
              <option value="week">Next 7 Days</option>
              <option value="month">Next 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Meeting Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''}
      </div>

      {/* Table - Desktop View */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Meeting Date</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Customer</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Cust. Phone</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Restaurant</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Phone</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Lunch Ordered</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Attendees</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Cost</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMeetings.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-gray-500">
                    No upcoming meetings found.
                  </td>
                </tr>
              ) : (
                filteredMeetings.map((meeting) => (
                  <tr key={meeting.id} className="hover:bg-gray-50 transition-colors">
                    {/* Meeting Date */}
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <button
                        onClick={() => handleEdit(meeting.id)}
                        className="text-left hover:text-blue-600 transition-colors"
                      >
                        <div className="font-medium hover:underline">{formatDate(meeting.meeting_date)}</div>
                        <div className="text-gray-500 text-xs mt-1">{formatTime(meeting.start_time)}</div>
                      </button>
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-4 text-sm text-center text-gray-900">
                      <button
                        onClick={() => {
                          setSelectedCustomer(meeting.customerData);
                          setShowCustomerModal(true);
                        }}
                        className="font-medium hover:text-blue-600 hover:underline transition-colors"
                      >
                        {meeting.customerName}
                      </button>
                    </td>

                    {/* Customer Phone */}
                    <td className="px-4 py-4 text-sm text-center whitespace-nowrap">
                      {meeting.customerPhone ? (
                        <a
                          href={`tel:${cleanPhoneForTel(meeting.customerPhone)}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {formatPhoneNumber(meeting.customerPhone)}
                        </a>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>

                    {/* Restaurant */}
                    <td className="px-4 py-4 text-sm text-center text-gray-900">
                      {meeting.restaurant_name ? (
                        <button
                          onClick={() => {
                            setSelectedRestaurant({
                              name: meeting.restaurant_name,
                              url: meeting.restaurant_url,
                              phone: meeting.restaurant_phone,
                              address: meeting.restaurant_address
                            });
                            setShowRestaurantModal(true);
                          }}
                          className="hover:text-blue-600 hover:underline transition-colors"
                        >
                          {meeting.restaurant_name}
                        </button>
                      ) : (
                        <span className="text-gray-400">TBD</span>
                      )}
                    </td>

                    {/* Restaurant Phone */}
                    <td className="px-4 py-4 text-sm text-center">
                      {meeting.restaurant_phone ? (
                        <a
                          href={`tel:${cleanPhoneForTel(meeting.restaurant_phone)}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {formatPhoneNumber(meeting.restaurant_phone)}
                        </a>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4 text-sm text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(meeting.status)}`}>
                        {meeting.status?.charAt(0).toUpperCase() + meeting.status?.slice(1) || 'Pending'}
                      </span>
                    </td>

                    {/* Lunch Ordered Checkbox */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <input
                          type="checkbox"
                          checked={meeting.lunch_ordered || false}
                          onChange={() => handleLunchOrderedToggle(meeting.id, meeting.lunch_ordered)}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        {savingStatus[meeting.id] === 'saving' && (
                          <span className="text-xs text-gray-500">Saving...</span>
                        )}
                        {savingStatus[meeting.id] === 'saved' && (
                          <span className="text-xs text-green-600">✓</span>
                        )}
                        {savingStatus[meeting.id] === 'error' && (
                          <span className="text-xs text-red-600">Error</span>
                        )}
                      </div>
                    </td>

                    {/* Attendees */}
                    <td className="px-4 py-4 text-sm text-center text-gray-900 font-medium">
                      {meeting.attendee_count || 2}
                    </td>

                    {/* Cost */}
                    <td className="px-4 py-4 text-sm">
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-1">$</span>
                        <input
                          type="text"
                          value={editingCost[meeting.id] !== undefined
                            ? editingCost[meeting.id]
                            : formatCurrencyWithCommas(meeting.actual_cost)}
                          onFocus={() => {
                            // Set raw value when focusing
                            setEditingCost(prev => ({
                              ...prev,
                              [meeting.id]: meeting.actual_cost ? meeting.actual_cost.toString() : ''
                            }));
                          }}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            // Only allow valid number characters
                            if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
                              setEditingCost(prev => ({
                                ...prev,
                                [meeting.id]: rawValue
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const rawValue = e.target.value;
                            handleCostUpdate(meeting.id, rawValue);
                            // Clear editing state
                            setEditingCost(prev => {
                              const newState = { ...prev };
                              delete newState[meeting.id];
                              return newState;
                            });
                          }}
                          placeholder="0.00"
                          className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm text-right"
                        />
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <button
                          onClick={() => handlePrintAttendeeList(meeting.id)}
                          disabled={loadingAttendeeList}
                          className="text-gray-600 hover:text-blue-600 transition-colors disabled:opacity-50"
                          title="Print Attendee List"
                        >
                          <Printer className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(meeting.id)}
                          className="text-gray-600 hover:text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View - Cards */}
      <div className="lg:hidden space-y-4">
        {filteredMeetings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
            No upcoming meetings found.
          </div>
        ) : (
          filteredMeetings.map((meeting) => (
            <div key={meeting.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <button
                  onClick={() => handleEdit(meeting.id)}
                  className="text-left"
                >
                  <div className="font-semibold text-gray-900 hover:text-blue-600 hover:underline">{formatDate(meeting.meeting_date)}</div>
                  <div className="text-sm text-gray-500">{formatTime(meeting.start_time)}</div>
                </button>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(meeting.status)}`}>
                  {meeting.status?.charAt(0).toUpperCase() + meeting.status?.slice(1) || 'Pending'}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4">
                <div>
                  <span className="text-xs text-gray-500">Customer:</span>
                  <button
                    onClick={() => {
                      setSelectedCustomer(meeting.customerData);
                      setShowCustomerModal(true);
                    }}
                    className="block text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline"
                  >
                    {meeting.customerName}
                  </button>
                  {meeting.customerPhone && (
                    <a href={`tel:${cleanPhoneForTel(meeting.customerPhone)}`} className="text-sm text-blue-600">
                      {formatPhoneNumber(meeting.customerPhone)}
                    </a>
                  )}
                </div>

                <div>
                  <span className="text-xs text-gray-500">Restaurant:</span>
                  {meeting.restaurant_name ? (
                    <button
                      onClick={() => {
                        setSelectedRestaurant({
                          name: meeting.restaurant_name,
                          url: meeting.restaurant_url,
                          phone: meeting.restaurant_phone,
                          address: meeting.restaurant_address
                        });
                        setShowRestaurantModal(true);
                      }}
                      className="block text-sm text-gray-900 hover:text-blue-600 hover:underline"
                    >
                      {meeting.restaurant_name}
                    </button>
                  ) : (
                    <div className="text-sm text-gray-900">TBD</div>
                  )}
                  {meeting.restaurant_phone && (
                    <a href={`tel:${cleanPhoneForTel(meeting.restaurant_phone)}`} className="text-sm text-blue-600">
                      {formatPhoneNumber(meeting.restaurant_phone)}
                    </a>
                  )}
                </div>

                {/* Lunch Ordered & Attendees */}
                <div className="flex items-center space-x-6 pt-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={meeting.lunch_ordered || false}
                      onChange={() => handleLunchOrderedToggle(meeting.id, meeting.lunch_ordered)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Lunch Ordered</span>
                  </label>

                  <div className="text-sm text-gray-700">
                    <span className="font-medium">Attendees:</span> {meeting.attendee_count || 2}
                  </div>
                </div>

                {/* Cost */}
                <div className="flex items-center space-x-2 pt-2">
                  <span className="text-sm text-gray-700 font-medium">Cost:</span>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-1">$</span>
                    <input
                      type="text"
                      value={editingCost[meeting.id] !== undefined
                        ? editingCost[meeting.id]
                        : formatCurrencyWithCommas(meeting.actual_cost)}
                      onFocus={() => {
                        // Set raw value when focusing
                        setEditingCost(prev => ({
                          ...prev,
                          [meeting.id]: meeting.actual_cost ? meeting.actual_cost.toString() : ''
                        }));
                      }}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        // Only allow valid number characters
                        if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
                          setEditingCost(prev => ({
                            ...prev,
                            [meeting.id]: rawValue
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        const rawValue = e.target.value;
                        handleCostUpdate(meeting.id, rawValue);
                        // Clear editing state
                        setEditingCost(prev => {
                          const newState = { ...prev };
                          delete newState[meeting.id];
                          return newState;
                        });
                      }}
                      placeholder="0.00"
                      className="w-28 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm text-right"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-3 border-t border-gray-200">
                <button
                  onClick={() => handlePrintAttendeeList(meeting.id)}
                  disabled={loadingAttendeeList}
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-blue-600 disabled:opacity-50"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print</span>
                </button>
                <button
                  onClick={() => handleEdit(meeting.id)}
                  className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Printable Attendee List (hidden on screen, visible when printing) */}
      {attendeeListData && (
        <PrintableAttendeeList attendeeData={attendeeListData} />
      )}

      {/* Customer Details Modal */}
      {showCustomerModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Customer Details</h2>
              <button
                onClick={() => setShowCustomerModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Name</label>
                  <p className="text-lg text-gray-900">
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </p>
                </div>

                {selectedCustomer.title && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Title</label>
                    <p className="text-lg text-gray-900">{selectedCustomer.title}</p>
                  </div>
                )}

                {selectedCustomer.company && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Company</label>
                    <p className="text-lg text-gray-900">{selectedCustomer.company}</p>
                  </div>
                )}

                {selectedCustomer.specialty && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Specialty</label>
                    <p className="text-lg text-gray-900">{selectedCustomer.specialty}</p>
                  </div>
                )}

                {selectedCustomer.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Phone</label>
                    <p className="text-lg">
                      <a
                        href={`tel:${cleanPhoneForTel(selectedCustomer.phone)}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {formatPhoneNumber(selectedCustomer.phone)}
                      </a>
                    </p>
                  </div>
                )}

                {selectedCustomer.email && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-lg">
                      <a
                        href={`mailto:${selectedCustomer.email}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {selectedCustomer.email}
                      </a>
                    </p>
                  </div>
                )}

                {selectedCustomer.npi_number && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-600">NPI Number</label>
                    <p className="text-lg text-gray-900 font-mono">{selectedCustomer.npi_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowCustomerModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restaurant Details Modal */}
      {showRestaurantModal && selectedRestaurant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Restaurant Details</h2>
              <button
                onClick={() => setShowRestaurantModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Restaurant Name</label>
                  <p className="text-xl font-semibold text-gray-900">{selectedRestaurant.name}</p>
                </div>

                {selectedRestaurant.address && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Address</label>
                    <p className="text-lg text-gray-900">{selectedRestaurant.address}</p>
                  </div>
                )}

                {selectedRestaurant.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Phone</label>
                    <p className="text-lg">
                      <a
                        href={`tel:${cleanPhoneForTel(selectedRestaurant.phone)}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {formatPhoneNumber(selectedRestaurant.phone)}
                      </a>
                    </p>
                  </div>
                )}

                {selectedRestaurant.url && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Yelp Page</label>
                    <p className="text-lg">
                      <a
                        href={selectedRestaurant.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center space-x-1"
                      >
                        <span>View on Yelp</span>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowRestaurantModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  );
};

export default OrderManagementView;
